import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useOpenEnrollmentPeriodApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// status → list of allowed next states (mirror of backend AllowedTransitions)
const NEXT_STATES = {
  draft: ['active'],
  active: ['reminding', 'closed'],
  reminding: ['closed'],
  closed: ['activated'],
  activated: [],
};

export default function OpenEnrollmentPeriodsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [statusConfirm, setStatusConfirm] = useState({ isOpen: false, id: null, target: null, name: '' });
  const [deleting, setDeleting] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete, changeStatus } = useOpenEnrollmentPeriodApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({
      ...row,
      startDate: row.startDate ? row.startDate.split('T')[0] : '',
      endDate: row.endDate ? row.endDate.split('T')[0] : '',
    });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('openEnrollment.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleStatusConfirm = useCallback(async () => {
    setStatusChanging(true);
    await changeStatus(statusConfirm.id, statusConfirm.target);
    setStatusConfirm({ isOpen: false, id: null, target: null, name: '' });
    setStatusChanging(false);
  }, [changeStatus, statusConfirm]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const buildActions = useCallback((row) => {
    if (!canWrite('BN')) return [];
    const actions = [
      { type: 'edit', label: t('openEnrollment.edit'), onClick: handleEdit },
    ];
    const nexts = NEXT_STATES[row.status] || [];
    nexts.forEach((next) => {
      actions.push({
        label: t(`openEnrollment.transition${next.charAt(0).toUpperCase()}${next.slice(1)}`),
        onClick: (r) => setStatusConfirm({ isOpen: true, id: r.id, target: next, name: r.name }),
      });
    });
    actions.push({ type: 'divider' });
    actions.push({ type: 'delete', label: t('openEnrollment.delete'), onClick: handleDelete });
    return actions;
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            if (actions.length === 0) return null;
            return <OptionsMenu actions={actions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, buildActions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('openEnrollment.edit') : t('openEnrollment.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('openEnrollment.deleteTitle')}
        message={t('openEnrollment.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <ConfirmDialog
        isOpen={statusConfirm.isOpen}
        title={t('openEnrollment.transitionTitle')}
        message={t('openEnrollment.transitionMessage', {
          name: statusConfirm.name,
          target: statusConfirm.target ? t(`openEnrollment.status${statusConfirm.target.charAt(0).toUpperCase()}${statusConfirm.target.slice(1)}`) : '',
        })}
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusConfirm({ isOpen: false, id: null, target: null, name: '' })}
        loading={statusChanging}
      />

      {useMemo(() => (
        <DataTable
          title={t('openEnrollment.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('BN')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('BN') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
